"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { hasUnreadTickets } from "../actions/messages";
import type { AuthState, SubscriptionsInfo, UserInfo } from "../types";

type AppContextType = {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  userInfo: UserInfo;
  setUserInfo: (info: UserInfo) => void;
  auth: AuthState;
  setAuth: (state: AuthState) => void;
  onLogout: () => void;
  setOnLogout: (fn: () => void) => void;
  subscriptionsInfo: SubscriptionsInfo | null;
  setSubscriptionsInfo: (info: SubscriptionsInfo | null) => void;
  /** At least one message thread holds something the customer has not opened. */
  hasUnreadMessages: boolean;
  /** Re-asks the server, e.g. right after opening a thread marks it read. */
  refreshUnreadMessages: () => void;
};

/** How often the header dot re-asks the server while the tab is in front. */
const POLL_MS = 30_000;

const defaultUserInfo: UserInfo = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  phoneCountryCode: "",
  documentId: "",
  passportId: "",
  avatarId: "",
};

const defaultAuth: AuthState = {
  isAuthenticated: false,
  isLoading: true,
};

const AppContext = createContext<AppContextType>({
  sidebarCollapsed: false,
  setSidebarCollapsed: () => {},
  userInfo: defaultUserInfo,
  setUserInfo: () => {},
  auth: defaultAuth,
  setAuth: () => {},
  onLogout: () => {},
  setOnLogout: () => {},
  subscriptionsInfo: null,
  setSubscriptionsInfo: () => {},
  hasUnreadMessages: false,
  refreshUnreadMessages: () => {},
});

export function AppProvider({ children }: { children: ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo>(defaultUserInfo);
  const [auth, setAuth] = useState<AuthState>(defaultAuth);
  const [logoutFn, setLogoutFn] = useState<() => void>(() => () => {});
  const [subscriptionsInfo, setSubscriptionsInfo] =
    useState<SubscriptionsInfo | null>(null);

  const setOnLogout = useCallback((fn: () => void) => {
    setLogoutFn(() => fn);
  }, []);

  // Asked of the server rather than derived from the list on screen: the
  // messages page shows one page of seven, and the header dot has to speak for
  // every page. A signed-out visitor gets `false` and no dot, which is right.
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);

  const refreshUnreadMessages = useCallback(() => {
    hasUnreadTickets()
      .then(setHasUnreadMessages)
      .catch(() => setHasUnreadMessages(false));
  }, []);

  // On mount, every time the tab comes back to the front, and on a slow poll
  // for the customer who just leaves it open. Support replies land minutes
  // after the page did, and the dot has to arrive with them rather than on the
  // next hard refresh (GD-93).
  useEffect(() => {
    // ponytail: polling beats a socket for mail that arrives by email. The
    // read behind it is GAPI-906's own unread endpoint, which answers in one
    // boolean — cheap enough to ask on a timer.
    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") refreshUnreadMessages();
    };

    refreshUnreadMessages();
    document.addEventListener("visibilitychange", refreshIfVisible);
    const poll = setInterval(refreshIfVisible, POLL_MS);

    return () => {
      document.removeEventListener("visibilitychange", refreshIfVisible);
      clearInterval(poll);
    };
  }, [refreshUnreadMessages]);

  const value = useMemo(
    () => ({
      sidebarCollapsed,
      setSidebarCollapsed,
      userInfo,
      setUserInfo,
      auth,
      setAuth,
      onLogout: logoutFn,
      setOnLogout,
      subscriptionsInfo,
      setSubscriptionsInfo,
      hasUnreadMessages,
      refreshUnreadMessages,
    }),
    [
      sidebarCollapsed,
      userInfo,
      auth,
      logoutFn,
      setOnLogout,
      subscriptionsInfo,
      hasUnreadMessages,
      refreshUnreadMessages,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext() {
  return useContext(AppContext);
}
